import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BellIcon, ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useChangelog } from '../Changelog/ChangelogProvider';
import { ChangelogModal } from '../../ui/atoms';
import { cleanHtmlContent } from '../Changelog/utils';
import { formatDisplayDateTime } from '../../utils/dateFormatting';

export default function NotificationsDrawerContent({ onClose }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isChangelogModalOpen, setIsChangelogModalOpen] = useState(false);
  const [clickedChangelogIds, setClickedChangelogIds] = useState(() =>
    JSON.parse(localStorage.getItem('clicked_changelog_ids') || '[]')
  );
  const {
    allChangelogs,
    loadAllChangelogs
  } = useChangelog();

  useEffect(() => {
    loadAllChangelogs();
  }, [loadAllChangelogs]);
  const lastSeenId = Number(localStorage.getItem('last_seen_changelog_id') || '0');
  const locale = localStorage.getItem('i18nextLng') || 'en';

  const allNotifications = allChangelogs.slice(0, 20).map((changelog) => {
    const isNew = changelog.id > lastSeenId && !clickedChangelogIds.includes(changelog.id);
    const isClicked = clickedChangelogIds.includes(changelog.id);
    return {
      id: `changelog-${changelog.id}`,
      type: 'changelog',
      title: isNew ? t('notifications.newUpdates') : t('notifications.platformUpdates'),
      message: `${cleanHtmlContent(changelog.changelog_excerpt).substring(0, 100)}...` || t('notifications.checkLatest'),
      time: isNew ? t('notifications.new') : formatDisplayDateTime(changelog.date_created, locale),
      isNew,
      isClicked,
      data: changelog
    };
  });

  const handleChangelogClick = (notification) => {
    setIsChangelogModalOpen(true);
    if (notification.data) {
      setClickedChangelogIds((current) => {
        if (current.includes(notification.data.id)) return current;
        const next = [...current, notification.data.id];
        localStorage.setItem('clicked_changelog_ids', JSON.stringify(next));
        return next;
      });
    }
  };

  const handleShowMore = () => {
    navigate('/notifications');
    onClose?.();
  };

  return (
    <div className="h-full flex flex-col bg-white font-sans">
      <div className="px-4 py-4 border-b border-gray-200 bg-white">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">{t('notifications.title')}</h2>
          <button
            type="button"
            className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
            onClick={onClose}
          >
            <span className="sr-only">Close panel</span>
            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {allNotifications.length > 0 ? (
          allNotifications.map((notification) => (
            <div
              key={notification.id}
              onClick={notification.type === 'changelog' ? () => handleChangelogClick(notification) : undefined}
              className={`px-6 py-4 border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                notification.type === 'changelog' ? 'cursor-pointer' : ''
              } ${notification.isNew ? 'bg-blue-50' : ''}`}
            >
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 mt-1">
                  <BellIcon className="h-5 w-5 text-gray-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-1">
                    <h4 className={`text-base leading-6 ${
                      notification.isNew ? 'font-semibold text-gray-900' : 'font-normal text-gray-700'
                    }`}
                    >
                      {notification.title}
                    </h4>
                    <span className={`text-xs px-2 py-1 rounded-full ml-3 flex-shrink-0 ${
                      notification.isNew ? 'bg-primary-100 text-primary-800 font-medium' : 'text-gray-500'
                    }`}
                    >
                      {notification.time}
                    </span>
                  </div>
                  <p className={`text-sm leading-6 ${
                    notification.isNew ? 'text-gray-700' : 'text-gray-500'
                  }`}
                  >
                    {notification.message}
                  </p>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center py-12">
              <BellIcon className="mx-auto h-12 w-12 text-gray-300" />
              <h3 className="mt-4 text-base font-medium text-gray-900">
                {t('notifications.noNotifications')}
              </h3>
              <p className="mt-2 text-sm text-gray-500">
                {t('notifications.noNotificationsDesc')}
              </p>
            </div>
          </div>
        )}
      </div>

      {allNotifications.length > 0 && (
        <div className="border-t border-gray-200 p-4 bg-gray-50">
          <button
            onClick={handleShowMore}
            className="w-full px-4 py-2 text-sm font-medium text-primary-600 hover:bg-primary-50 flex items-center justify-center space-x-2 transition-colors rounded-lg"
          >
            <span>{t('notifications.viewAll')}</span>
            <ChevronRightIcon className="h-4 w-4" />
          </button>
        </div>
      )}

      <ChangelogModal
        isOpen={isChangelogModalOpen}
        onClose={() => setIsChangelogModalOpen(false)}
      />
    </div>
  );
}
